import { MessageAppend } from '@bitte-ai/types';
import { generateId } from 'ai';
import React, { FC, memo, useState } from 'react';
import ReactMarkdown, { Options } from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { isMarkdownTableString } from '../../lib/regex';
import { cn } from '../../lib/utils';
import { MarkdownTable } from './MarkdownTable';

const MemoizedReactMarkdown: FC<Options> = memo(
  ReactMarkdown,
  (prevProps, nextProps) =>
    prevProps.children === nextProps.children &&
    prevProps.className === nextProps.className,
);

const LinkRenderer = ({
  href,
  children,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
  return (
    <a
      href={href as string}
      target="_blank"
      rel="noopener noreferrer"
      className="bitte-text-blue-300 hover:bitte-text-blue-100 bitte-underline bitte-transition-colors bitte-duration-200"
      {...props}
    >
      {children}
    </a>
  );
};

const HeadingRenderer = ({ level, children, ...props }: any) => {
  const headingClasses = {
    1: 'bitte-text-xl bitte-font-bold bitte-text-blue-50 bitte-mb-4 bitte-pb-2',
    2: 'bitte-text-[19px] bitte-font-semibold bitte-text-blue-50 bitte-mb-3 bitte-mt-6',
    3: 'bitte-text-base bitte-font-medium bitte-text-blue-100 bitte-mb-2 bitte-mt-4',
    4: 'bitte-text-sm bitte-font-medium bitte-text-blue-200 bitte-mb-2',
    5: 'bitte-text-[12px] bitte-font-medium bitte-text-blue-300 bitte-mb-1',
    6: 'bitte-text-[12px] bitte-font-normal bitte-text-blue-400 bitte-mb-1',
  };

  const Tag = `h${level}` as keyof JSX.IntrinsicElements;

  return (
    <Tag
      className={headingClasses[level as keyof typeof headingClasses]}
      {...props}
    >
      {children}
    </Tag>
  );
};

const ListRenderer = ({ ordered, children, ...props }: any) => {
  const Tag = ordered ? 'ol' : 'ul';
  return (
    <Tag
      className={cn(
        'bitte-space-y-1 bitte-mb-4 bitte-mt-2',
        ordered
          ? 'bitte-list-decimal bitte-list-inside '
          : 'bitte-list-disc bitte-list-inside ',
      )}
      {...props}
    >
      {children}
    </Tag>
  );
};

const ListItemRenderer = ({ children, ...props }: any) => {
  // Check if children contain paragraph elements and flatten them
  const flattenedChildren = React.Children.map(children, (child) => {
    // If child is a paragraph element, return its children instead
    if (React.isValidElement(child) && child.type === 'p') {
      const paragraphElement = child as React.ReactElement<{
        children: React.ReactNode;
      }>;
      return paragraphElement.props.children;
    }
    return child;
  });

  return (
    <li
      className="bitte-text-blue-100 bitte-text-[14px] bitte-leading-relaxed bitte-block bitte-break-words"
      style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}
      {...props}
    >
      {flattenedChildren}
    </li>
  );
};

// Enhanced component to handle clickable example text
const ExampleTextRenderer = ({
  content,
  onAppend,
}: {
  content: string;
  onAppend?: MessageAppend;
}) => {
  // Regex to match example patterns like:
  // (For example: "Swap 0.1 ETH to USDC on Arbitrum.")
  // (for example: "Supply USDT," "Borrow NEAR," "Swap tokens," or "Show my portfolio")
  const examplePattern = /\((For example|for example):\s*([^)]+)\)/gi;

  type ProcessedPart =
    | string
    | {
        type: 'clickable';
        text: string;
        content: string;
        key: string;
      };

  const processExampleText = (text: string): ProcessedPart[] => {
    const parts: ProcessedPart[] = [];
    let lastIndex = 0;
    let match;

    match = examplePattern.exec(text);
    while (match !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }

      // Process the example content
      const exampleContent = match[2];
      const quotedTexts = exampleContent.match(/"([^"]+)"/g) || [];

      // Build the example section
      const exampleStart = `(${match[1]}: `;
      const exampleEnd = ')';

      parts.push(exampleStart);

      // Process quoted texts and make them clickable
      let exampleLastIndex = 0;
      const tempContent = exampleContent;

      quotedTexts.forEach((quotedText, index) => {
        const unquotedText = quotedText.replace(/"/g, '');
        const quotedIndex = tempContent.indexOf(quotedText, exampleLastIndex);

        // Add text before the quoted text
        if (quotedIndex > exampleLastIndex) {
          parts.push(tempContent.substring(exampleLastIndex, quotedIndex));
        }

        // Add clickable quoted text
        parts.push({
          type: 'clickable',
          text: quotedText,
          content: unquotedText,
          key: `example-${index}-${Math.random()}`,
        });

        exampleLastIndex = quotedIndex + quotedText.length;
      });

      // Add remaining text after the last quoted text
      if (exampleLastIndex < exampleContent.length) {
        parts.push(exampleContent.substring(exampleLastIndex));
      }

      parts.push(exampleEnd);
      lastIndex = match.index + match[0].length;
      match = examplePattern.exec(text);
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts;
  };

  const handleExampleClick = (exampleText: string) => {
    if (onAppend) {
      onAppend({
        id: generateId(),
        role: 'user',
        content: exampleText,
      });
    }
  };

  const processedParts = processExampleText(content);

  return (
    <span>
      {processedParts.map((part, index) => {
        if (typeof part === 'string') {
          return <span key={index}>{part}</span>;
        } else if (part.type === 'clickable') {
          return (
            <button
              key={part.key}
              type="button"
              onClick={() => handleExampleClick(part.content)}
              className="bitte-text-blue-400 hover:bitte-text-blue-300 bitte-underline bitte-cursor-pointer bitte-transition-colors bitte-duration-200 bitte-bg-transparent bitte-border-none bitte-p-0 bitte-font-inherit"
              title={`Click to try: ${part.content}`}
            >
              {part.text}
            </button>
          );
        }
        return null;
      })}
    </span>
  );
};

// Updated ParagraphRenderer to handle example text
const ParagraphRenderer = ({ children, onAppend, ...props }: any) => {
  const content = React.Children.toArray(children).join('');
  const hasExamplePattern = /\((For example|for example):/i.test(content);

  if (hasExamplePattern && onAppend) {
    return (
      <p
        className="bitte-text-blue-100 bitte-text-[14px] bitte-block bitte-leading-relaxed bitte-break-words"
        style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}
        {...props}
      >
        <ExampleTextRenderer content={content} onAppend={onAppend} />
      </p>
    );
  }

  return (
    <p
      className="bitte-text-blue-100 bitte-text-[14px]  bitte-block bitte-leading-relaxed bitte-break-words"
      style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}
      {...props}
    >
      {children}
    </p>
  );
};

const CodeRenderer = ({ inline, children, className }: any) => {
  const [isCopied, setIsCopied] = useState(false);

  // Helper function to recursively extract text content from React elements
  const extractTextContent = (node: any): string => {
    if (typeof node === 'string') {
      return node;
    }
    if (typeof node === 'number') {
      return String(node);
    }
    if (Array.isArray(node)) {
      return node.map(extractTextContent).join('');
    }
    if (React.isValidElement(node)) {
      const element = node as React.ReactElement<{
        children?: React.ReactNode;
      }>;
      if (typeof element.props.children !== 'undefined') {
        return extractTextContent(element.props.children);
      }
    }
    return '';
  };

  const handleCopy = async () => {
    try {
      const textContent = extractTextContent(children);
      await navigator.clipboard.writeText(textContent.trim());
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000); // Hide after 2 seconds
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  // Extract language from className (e.g., "language-javascript" -> "javascript")
  const language = (className?.replace('language-', '') || 'text')
    .replace(/^hljs[\s-]*/i, '') // Remove hljs prefix
    .replace(/^language[\s-]*/i, ''); // Remove language prefix

  if (inline) {
    return (
      <code
        className="bitte-bg-blue-950/80 bitte-text-blue-200 bitte-px-2 bitte-py-1 bitte-rounded bitte-text-[12px] bitte-font-mono bitte-border bitte-border-blue-400/30"
        style={{
          border: 'none !important',
          borderRadius: '0.25rem !important',
          padding: '0.25rem 0.5rem !important',
        }}
      >
        {children}
      </code>
    );
  }

  return (
    <div className="bitte-relative bitte-my-6 bitte-group">
      <div className="bitte-bg-blue-950/80 bitte-border bitte-border-blue-400/30 bitte-rounded-lg bitte-overflow-hidden">
        {/* Language label and copy button header */}
        <div className="bitte-flex bitte-items-center bitte-justify-between bitte-px-4 bitte-py-2 bitte-bg-blue-900/50 bitte-border-b bitte-border-blue-400/20">
          <span className="bitte-text-blue-300 bitte-text-[12px] bitte-font-medium bitte-uppercase bitte-tracking-wide">
            {language}
          </span>
          <button
            type="button"
            onClick={handleCopy}
            className="bitte-flex bitte-items-center bitte-gap-1 bitte-px-2 bitte-py-1 bitte-text-[12px] bitte-text-blue-300 hover:bitte-text-blue-200 bitte-bg-blue-800/50 hover:bitte-bg-blue-700/50 bitte-border bitte-border-blue-400/30 bitte-rounded bitte-transition-all bitte-duration-200"
            title="Copy code"
          >
            {isCopied ? (
              <>
                <svg
                  className="bitte-w-3 bitte-h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  role="img"
                >
                  <title>Checkmark icon</title>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span>Copied!</span>
              </>
            ) : (
              <>
                <svg
                  className="bitte-w-3 bitte-h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  role="img"
                >
                  <title>Copy icon</title>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                <span>Copy</span>
              </>
            )}
          </button>
        </div>

        {/* Code content */}
        <div
          className="bitte-p-4 bitte-overflow-x-auto bitte-m-0"
          style={{
            background: 'transparent !important',
            backgroundColor: 'transparent !important',
            backgroundImage: 'none !important',
          }}
        >
          <code
            className={`bitte-bg-blue-950/80 bitte-text-blue-100 bitte-text-[13px] bitte-font-mono bitte-leading-relaxed bitte-whitespace-pre bitte-block ${language ? `language-${language}` : ''}`}
            style={{
              background: 'rgb(30 41 59 / 0.8) !important',
              backgroundColor: 'rgb(30 41 59 / 0.8) !important',
              whiteSpace: 'pre',
              display: 'block',
              border: 'none !important',
              borderRadius: '0 !important',
              padding: '0 !important',
            }}
          >
            {children}
          </code>
        </div>
      </div>
    </div>
  );
};

const BlockquoteRenderer = ({ children, ...props }: any) => {
  return (
    <blockquote
      className="bitte-border-l-4 bitte-border-blue-500 bitte-pl-4 bitte-my-4 bitte-italic bitte-text-blue-200 bitte-bg-blue-950/30 bitte-py-2"
      {...props}
    >
      {children}
    </blockquote>
  );
};

// Table components for ReactMarkdown
const TableRenderer = ({ children, ...props }: any) => {
  return (
    <div className="bitte-relative bitte-w-full bitte-overflow-auto">
      <table
        className="bitte-mt-4 bitte-w-full bitte-caption-bottom bitte-text-sm"
        {...props}
      >
        {children}
      </table>
    </div>
  );
};

const TableHeaderRenderer = ({ children, ...props }: any) => {
  return (
    <thead className="[&_tr]:bitte-border-b" {...props}>
      {children}
    </thead>
  );
};

const TableBodyRenderer = ({ children, ...props }: any) => {
  return (
    <tbody className="[&_tr:last-child]:bitte-border-0" {...props}>
      {children}
    </tbody>
  );
};

const TableRowRenderer = ({ children, ...props }: any) => {
  return (
    <tr className="bitte-border-none hover:bitte-bg-transparent" {...props}>
      {children}
    </tr>
  );
};

const TableCellRenderer = ({ children, ...props }: any) => {
  return (
    <td
      className="bitte-whitespace-nowrap bitte-px-4 bitte-text-left bitte-p-2 bitte-align-middle"
      {...props}
    >
      <div className="bitte-max-w-[250px] bitte-break-words">
        <div className="bitte-truncate">{children}</div>
      </div>
    </td>
  );
};

const TableHeadCellRenderer = ({ children, ...props }: any) => {
  return (
    <th
      className="bitte-whitespace-nowrap bitte-px-4 bitte-text-left text-[14px] bitte-font-medium bitte-h-10 bitte-align-middle bitte-text-muted-foreground"
      {...props}
    >
      {children}
    </th>
  );
};

// HR renderer that doesn't render anything
const HRRenderer = () => {
  return null;
};

// Pre renderer to handle the outer pre element created by rehype-highlight
const PreRenderer = ({ children, ...props }: any) => {
  return (
    <pre
      className="bitte-bg-transparent"
      style={{
        background: 'transparent !important',
        backgroundColor: 'transparent !important',
      }}
      {...props}
    >
      {children}
    </pre>
  );
};

// Helper function to determine if content should be treated as table-only
const isTableOnlyContent = (content: string): boolean => {
  const lines = content.split('\n').filter((line) => line.trim() !== '');
  const tableLines = lines.filter((line) => line.trim().startsWith('|'));
  const separatorLines = lines.filter((line) =>
    line.trim().match(/^\|?[\s\-\|]+\|?$/),
  );

  // If less than 3 lines total, it's probably not a complex table
  if (lines.length < 3) return false;

  // Count non-table, non-separator lines that have meaningful content
  const nonTableLines = lines.filter((line) => {
    const trimmed = line.trim();
    return (
      !trimmed.startsWith('|') &&
      !trimmed.match(/^\|?[\s\-\|]+\|?$/) &&
      trimmed.length > 0
    );
  });

  // If there are more than 2 non-table lines, it's mixed content
  if (nonTableLines.length > 2) return false;

  // If table lines make up less than 50% of total lines, it's mixed content
  const tableRatio = (tableLines.length + separatorLines.length) / lines.length;
  return tableRatio > 0.5;
};

export const ImprovedMessage = memo(
  ({
    content,
    onAppend,
  }: {
    content: string;
    onAppend?: MessageAppend;
    isComplete?: boolean;
  }) => {
    const cleanContent = content
      .replace(/^"/, '') // remove first quote
      .replace(/"$/, '') // remove last quote
      .replace(/^`([\s\S]*)`$/g, '$1') // remove backticks that wrap entire content
      .replace(/\\n/g, '\n')
      .replace(/`([A-Za-z][A-Za-z0-9]*)`/g, '$1') // remove backticks around single words that look like tokens
      .replace(/`(st[A-Z][A-Za-z]*|li[A-Z][A-Za-z]*)`/g, '$1') // specifically handle stNEAR, liNEAR type tokens
      .replace(/\\`/g, '`') // unescape escaped backticks
      .replace(/\n{3,}/g, '\n\n') // replace multiple newlines with double newlines
      .replace(/<img\s+src="([^"]+)"[^>]*>/gi, '![]($1)') // convert HTML img tags to markdown (case insensitive)
      .replace(/<img\s+src='([^']+)'[^>]*>/gi, '![]($1)') // handle single quotes too
      .trim(); // remove leading/trailing whitespace

    // For table content - only use MarkdownTable if content is predominantly table
    if (
      isMarkdownTableString(cleanContent) &&
      isTableOnlyContent(cleanContent)
    ) {
      return <MarkdownTable content={cleanContent} />;
    }

    // For regular markdown content (including content with tables mixed with other content)
    return (
      <div
        className="[&_code]:bitte-text-blue-200 [&_code]:bitte-px-2 [&_code]:bitte-py-1 [&_code]:bitte-rounded [&_code]:bitte-text-[12px] [&_code]:bitte-font-mono [&_.hljs-keyword]:bitte-text-purple-400 [&_.hljs-string]:bitte-text-emerald-400 [&_.hljs-comment]:bitte-text-gray-500 [&_.hljs-number]:bitte-text-orange-400 [&_.hljs-attr]:bitte-text-cyan-400 [&_.hljs-function]:bitte-text-yellow-400 [&_.hljs-variable]:bitte-text-blue-300 [&_.hljs-title]:bitte-text-pink-400"
        style={{
          ['--code-background' as any]: 'rgb(30 41 59 / 0.8)',
        }}
      >
        <style>
          {`

            .bitte-max-w-none pre , .bitte-max-w-none code{
              background: transparent !important;
              background-color: transparent  !important;
              border: none !important;
            }
          `}
        </style>
        <MemoizedReactMarkdown
          className="bitte-max-w-none"
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[
            rehypeRaw,
            [
              rehypeHighlight,
              {
                detect: true,
                ignoreMissing: true,
                subset: false,
              },
            ],
          ]}
          components={{
            a: LinkRenderer,
            h1: (props) => <HeadingRenderer level={1} {...props} />,
            h2: (props) => <HeadingRenderer level={2} {...props} />,
            h3: (props) => <HeadingRenderer level={3} {...props} />,
            h4: (props) => <HeadingRenderer level={4} {...props} />,
            h5: (props) => <HeadingRenderer level={5} {...props} />,
            h6: (props) => <HeadingRenderer level={6} {...props} />,
            p: (props) => <ParagraphRenderer {...props} onAppend={onAppend} />,
            ul: (props) => <ListRenderer ordered={false} {...props} />,
            ol: (props) => <ListRenderer ordered={true} {...props} />,
            li: ListItemRenderer,
            code: (props: any) => <CodeRenderer {...props} />,
            pre: PreRenderer,
            blockquote: BlockquoteRenderer,
            img: ({ src, alt }) =>
              src ? (
                <img
                  src={src}
                  alt={alt || `${src} image`}
                  className="bitte-max-w-[70px] bitte-mr-3 bitte-rounded-[5px] bitte-inline-block bitte-align-top"
                />
              ) : (
                <></>
              ),
            table: TableRenderer,
            thead: TableHeaderRenderer,
            tbody: TableBodyRenderer,
            tr: TableRowRenderer,
            td: TableCellRenderer,
            th: TableHeadCellRenderer,
            hr: HRRenderer,
          }}
        >
          {cleanContent}
        </MemoizedReactMarkdown>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if the content actually changed
    if (prevProps.content !== nextProps.content) {
      return false;
    }

    // Only re-render if isComplete status changed
    if (prevProps.isComplete !== nextProps.isComplete) {
      return false;
    }

    // For onAppend, we compare the function reference
    // If it's the same function, we don't need to re-render
    if (prevProps.onAppend !== nextProps.onAppend) {
      return false;
    }

    // If none of the essential props changed, skip re-render
    return true;
  },
);

ImprovedMessage.displayName = 'ImprovedMessage';
